// ORDER-AWARE feasibility for multi-select filter cards.
//
// Otto's card now lets the user select SEVERAL filters (numbered in click order) and apply them
// together. The dashboard ANDs across dimensions and ORs within one, so a perfectly valid pair of
// individual options can produce an EMPTY dashboard together ("Roofing" ∧ "Renewal" = 0 records).
// These helpers verify the combined selection against the client's REAL data BEFORE apply, using the
// same /filter_catalog D3 cascade the dashboard's own dropdowns (and the existing cascaded-count
// guard) use — grounding principle: never let a bounded combination through unvalidated when we CAN
// validate it.
//
// Shape of the check (mirrors orchestrator's cascaded-count guard, generalized to in-card selections):
//   - Build ONE cascade from: the dashboard's applied filters (base) + the card's selected options
//     (grouped per dimension, honoring the intent's set/add/remove action) + the proposal's year.
//   - Fetch cascaded option counts once for the full cascade, then once per SELECTED/FILTERED field
//     with that field's own dimension excluded (values within a dimension are OR'd, so a field must be
//     counted under the OTHERS only — same-dimension exclusion).
//   - A selected dimension whose selected options ALL count 0 under the others is a CONFLICT: the
//     LATEST-numbered selection in it is the breaker (order attribution — the user built the set in
//     that order), and the field's options that DO have results are the viable alternatives.
//   - Text picks ("search descriptions for …") are UNVERIFIABLE: they never block and never count.
//     Wildcard/threshold filters (cost etc.) and fields the catalog doesn't cover ARE counted — via
//     the dashboard's own /get_list (see dynamicOptions), not the catalog cascade.
//
// Everything except runFeasibilityCheck is pure and unit-tested without the network.

import { fetchCascadedOptions } from "./catalog";
import {
    fetchDynamicOptionCounts,
    isGetListCountable,
    isGetListRecountable,
    metaByGlf,
    modelPostApplyRawParams,
    recountableMetaByGlf
} from "./dynamicOptions";

const glfKeyOf = meta => (meta && Array.isArray(meta.get_list_field) ? meta.get_list_field.join("|") : "");

// The Status dimension partitions the dashboard's visible records (exactly one status per record under
// the default scope) — the counting basis for a total when no dimension is selected (year-only).
const PARTITION_GLF = "recommendations.status";

// The dashboard's applied filters in /filter_catalog D3 shape, or null when nothing is applied.
// Requires `values` (the applied option ids) on activeFilters.list — dashboards that don't send them
// simply don't cascade. Lives HERE (not orchestrator.js) so the proposal card can build cascades
// without pulling in the whole orchestrator; orchestrator re-exports it for its own guard + tests.
export const buildCascade = activeFilters => {
    if (!activeFilters) return null;
    const dimensions = {};
    (activeFilters.list || []).forEach(item => {
        const glf = Array.isArray(item.get_list_field) ? item.get_list_field.join("|") : "";
        if (glf && Array.isArray(item.values) && item.values.length) dimensions[glf] = item.values.map(String);
    });
    const hasYear = activeFilters.start_year != null && activeFilters.end_year != null;
    if (!Object.keys(dimensions).length && !hasYear) return null;
    const cascade = { dimensions };
    if (hasYear) cascade.year = { start: activeFilters.start_year, end: activeFilters.end_year };
    return cascade;
};

/**
 * The dashboard cascade the card should verify against, shaped by the intent's own clearing actions:
 * `clear_all` wipes every applied filter on Apply (so nothing constrains the selections), and `clears`
 * drop specific dimensions. Without this, a "clear the trade filter and show plumbing" card would
 * wrongly count plumbing under the very trade filter it is about to remove.
 */
export const baseCascadeFor = (activeFilters, intent, maps) => {
    if (intent && intent.clear_all) return null;
    const cascade = buildCascade(activeFilters);
    if (!cascade || !intent || !(intent.clears || []).length) return cascade;
    const dimensions = { ...cascade.dimensions };
    (intent.clears || []).forEach(id => {
        const glf = glfKeyOf(maps && maps.dimMap && maps.dimMap[id]);
        if (glf) delete dimensions[glf];
    });
    if (!Object.keys(dimensions).length && !cascade.year) return null;
    return { ...cascade, dimensions };
};

// Stable identity for a match/pill — the same field+id+name triple sameMatch compares.
export const matchKey = m => `${m.field}|${m.id !== undefined && m.id !== null ? m.id : ""}|${m.name}`;

// A pick we can cascade: a real dimension option. Text picks (description/notes/search) become
// "contains" wildcards on apply — /filter_catalog can't count those, so they're never verified.
export const isDimensionPick = m => !!m && m.value_kind !== "text" && m.field !== "search";

// The apply action a selection will carry — mirrors buildApplyPayload's ref parsing exactly, so the
// feasibility cascade models the SAME dashboard state the apply would produce.
export const actionForRef = (ref, intent) => {
    if (typeof ref === "string" && ref.startsWith("c:")) {
        const idx = parseInt(ref.split(":")[1], 10);
        const cf = intent && intent.common_filters && intent.common_filters[idx];
        return (cf && cf.action) || "set";
    }
    return "set";
};

// The option value a selection puts on the dashboard — mirrors applyMapping (id, falling back to name
// for id-less value enums). Cascade arms and count lookups must use the SAME key.
const valueOf = m => String(m.id !== undefined && m.id !== null ? m.id : m.name);

/**
 * Group the ordered selections by dimension (get_list_field), keeping click orders and the apply
 * action split (set/add/remove). Non-dimension picks and fields without a glf are skipped.
 * Returns { [glf]: { field, set: [], add: [], remove: [], picks: [{ match, order, ref }] } }.
 */
export const groupSelections = (ordered, intent, maps) => {
    const groups = {};
    (ordered || []).forEach((sel, i) => {
        const m = sel.match;
        if (!isDimensionPick(m)) return;
        const meta = maps && maps.dimMap && maps.dimMap[m.field];
        const glf = glfKeyOf(meta);
        if (!glf) return;
        const g = (groups[glf] = groups[glf] || { field: m.field, set: [], add: [], remove: [], picks: [] });
        const action = actionForRef(sel.ref, intent);
        g[action === "add" ? "add" : action === "remove" ? "remove" : "set"].push(valueOf(m));
        // Removes subtract an existing filter — they can't "have no results" themselves, so they don't
        // join the conflict-eligible picks (but they DO shape the cascade above).
        if (action !== "remove") g.picks.push({ match: m, order: i + 1, ref: sel.ref });
    });
    return groups;
};

// Per-bound year merge: the proposal's grounded year overrides the dashboard's applied year, exactly
// as apply writes start_year/end_year individually. `year` is {start_year,end_year}|null.
const mergeYear = (baseYear, year) => {
    const start = year && year.start_year != null ? year.start_year : baseYear ? baseYear.start : null;
    const end = year && year.end_year != null ? year.end_year : baseYear ? baseYear.end : null;
    return start != null && end != null ? { start, end } : null;
};

/**
 * Merge the dashboard's base cascade with the card's selection groups + year into ONE /filter_catalog
 * cascade — the filter state the dashboard would be in AFTER Apply. Action semantics mirror
 * applyMapping: `set` replaces the dimension's applied values, `add` unions, `remove` subtracts (a
 * dimension emptied by removes drops out). `clears`/`clear_all` are handled by the caller shaping
 * baseCascade. Returns null when there is nothing to cascade (then nothing can conflict).
 */
export const mergeSelectionCascade = (baseCascade, groups, year) => {
    const dimensions = { ...((baseCascade && baseCascade.dimensions) || {}) };
    Object.keys(groups || {}).forEach(glf => {
        const g = groups[glf];
        const base = dimensions[glf] || [];
        const next = g.set.length
            ? [...new Set([...g.set, ...g.add])] // set replaces the applied values; adds ride along
            : [...new Set([...base, ...g.add])].filter(v => !g.remove.includes(v));
        if (next.length) dimensions[glf] = next;
        else delete dimensions[glf];
    });
    const mergedYear = mergeYear(baseCascade && baseCascade.year, year);
    if (!Object.keys(dimensions).length && !mergedYear) return null;
    const cascade = { dimensions };
    if (mergedYear) cascade.year = mergedYear;
    return cascade;
};

// Base cascade minus one dimension (same-dimension exclusion helper).
const withoutGlf = (baseCascade, glf) => {
    if (!baseCascade || !baseCascade.dimensions || !(glf in baseCascade.dimensions)) return baseCascade;
    const dimensions = { ...baseCascade.dimensions };
    delete dimensions[glf];
    return { ...baseCascade, dimensions };
};

/**
 * Pure evaluation of the fetched cascaded options against the selections + the card's pills.
 * `optionsByGlf` must already be same-dimension-excluded per field (runFeasibilityCheck does that).
 * Returns:
 *   counts:        { matchKey -> count } for every VERIFIABLE pill in `allMatches` (absent = unverifiable)
 *   viableByField: { field -> [{id,name,count}] } — the field's options that still have results,
 *                  best-first (feeds "here's what IS possible" for conflicts AND blocked pill taps)
 *   conflicts:     [{ field, glf, picks, breaker: {match, order}, alternatives: [{id,name,count}] }] —
 *                  selected dimensions whose selected options ALL read 0 under the other selections.
 */
export const evaluateFeasibility = ({ groups, optionsByGlf, allMatches, maps }) => {
    const countIndexes = {}; // glf -> { valueKey -> count }
    const indexFor = glf => {
        if (!countIndexes[glf]) {
            const list = optionsByGlf[glf];
            if (!list) return null;
            const byKey = {};
            list.forEach(o => {
                byKey[String(o.id !== null && o.id !== undefined ? o.id : o.name)] = o.count || 0;
            });
            countIndexes[glf] = byKey;
        }
        return countIndexes[glf];
    };
    const countOf = m => {
        if (!isDimensionPick(m)) return null;
        const glf = glfKeyOf(maps && maps.dimMap && maps.dimMap[m.field]);
        const byKey = glf && indexFor(glf);
        if (!byKey) return null; // field not covered by the catalog -> unverifiable
        return byKey[valueOf(m)] || 0;
    };

    const viableFor = glf =>
        (optionsByGlf[glf] || []).filter(o => (o.count || 0) > 0).sort((a, b) => (b.count || 0) - (a.count || 0));

    const counts = {};
    const viableByField = {};
    (allMatches || []).forEach(m => {
        const c = countOf(m);
        if (c !== null) counts[matchKey(m)] = c;
        if (c !== null && !viableByField[m.field]) {
            viableByField[m.field] = viableFor(glfKeyOf(maps && maps.dimMap && maps.dimMap[m.field]));
        }
    });

    const conflicts = [];
    Object.keys(groups || {}).forEach(glf => {
        const g = groups[glf];
        if (!g.picks.length) return;
        const pickCounts = g.picks.map(p => countOf(p.match));
        if (pickCounts.some(c => c === null)) return; // unverifiable -> never block
        if (pickCounts.some(c => c > 0)) return; // the OR-group still has results
        // Order attribution: the LATEST selection in the empty group is the one that "broke" the set.
        const breaker = g.picks.reduce((a, b) => (b.order > a.order ? b : a));
        conflicts.push({ field: g.field, glf, picks: g.picks, breaker, alternatives: viableFor(glf) });
    });
    return { counts, viableByField, conflicts };
};

/**
 * The full async check. `ordered` is the card's numbered selections; `allMatches` every pill we want a
 * live count for (all suggestions across the card). Fetches the full cascade once, plus one exclusion
 * call per field that is both in the cascade and on the card; DYNAMIC (inline-option) fields are counted
 * via the dashboard's own /get_list instead (see dynamicOptions), conditioned on `rawParams` — the
 * dashboard's raw `dashboardFilterParams` — modeled forward to the post-Apply state. Returns
 * evaluateFeasibility's result PLUS `total` — the record count under the FULL post-Apply state (the
 * "N Records" Otto shows by Apply). `total` is null when it can't be trusted: the backend didn't report
 * it (older deploy) OR the cascade couldn't count one of the selected/applied filters and we couldn't
 * recover it from /get_list either (an FCI/EFCI band, a raw-less caller) — a number that ignores a
 * filter would MISLEAD (5,066 for an FCI pick that applies to 1,617). `uncounted` is surfaced so the
 * card can also skip validation claims for those. Returns null when there is nothing to verify. Throws
 * only what fetch throws — the caller degrades any failure to "can't verify".
 */
export async function runFeasibilityCheck({
    scope,
    baseCascade,
    year,
    ordered,
    intent,
    maps,
    allMatches,
    rawParams,
    wildcards,
    filterFields
}) {
    const groups = groupSelections(ordered, intent, maps);
    let full = mergeSelectionCascade(baseCascade, groups, year);
    // A selected WILDCARD (cost threshold etc.) is a real constraint even with nothing else selected —
    // it can't ride the catalog cascade, but the /get_list conditioning below carries it.
    const selectedWildcards = wildcards || [];
    if (!full && !selectedWildcards.length) return null;
    if (!full) full = { dimensions: {} };

    // DYNAMIC (inline-option) fields fall outside the /filter_catalog cascade (`uncounted_dimensions`) —
    // their real counts come from the dashboard's own /get_list instead (dynamicOptions mirrors the
    // FilterModal call). Each dynamic field is counted under the modeled POST-APPLY raw params — the
    // exact dashboard state these selections would produce — with itself excluded (same-dimension
    // exclusion is inherent to that call). Fired in parallel with the catalog fetches; a null postApply
    // (unmodelable state) or a failed fetch degrades that field to unverifiable, exactly as before.
    const postApply = modelPostApplyRawParams({
        raw: rawParams,
        baseApplied: !!baseCascade,
        cascade: full,
        touchedGlfs: Object.keys(groups),
        intent,
        maps,
        scope,
        wildcards: selectedWildcards,
        filterFields
    });
    const dynamicGlfs = new Set();
    Object.keys(full.dimensions).forEach(glf => {
        if (isGetListCountable(metaByGlf(maps, glf))) dynamicGlfs.add(glf);
    });
    (allMatches || []).forEach(m => {
        if (!isDimensionPick(m)) return;
        const meta = maps && maps.dimMap && maps.dimMap[m.field];
        if (isGetListCountable(meta)) dynamicGlfs.add(glfKeyOf(meta));
    });
    const dynamicByGlf = {};
    const dynamicPromise = postApply
        ? Promise.all(
              [...dynamicGlfs].map(async glf => {
                  const res = await fetchDynamicOptionCounts({ meta: metaByGlf(maps, glf), conditionParams: postApply });
                  if (res) dynamicByGlf[glf] = res;
              })
          )
        : Promise.resolve();

    // GREYING PARITY under an uncountable arm: when the post-apply cascade holds a dimension
    // /filter_catalog can't count (a selected/applied dynamic field — predictable client-side; an
    // FCI/EFCI band — known only from the response's `uncounted`), the catalog's per-option counts for
    // the OTHER offered fields ignore that arm and OVER-count, so a pill the uncountable pick empties
    // would never grey. Re-count exactly the fields OFFERED ON THE CARD via the dashboard's own
    // /get_list (one call per field, conditioned on the modeled post-apply params; own dimension
    // excluded by construction). A successful re-count REPLACES that field's catalog counts; a failed
    // one keeps them (over-counting never wrongly blocks).
    const recountMetas = {};
    (allMatches || []).forEach(m => {
        if (!isDimensionPick(m)) return;
        const meta = maps && maps.dimMap && maps.dimMap[m.field];
        const glf = glfKeyOf(meta);
        if (!glf || dynamicGlfs.has(glf) || recountMetas[glf]) return;
        // The suggestion may ride a schema-keyed meta without the dashboard routing flags — its
        // dashboard twin (same get_list_field) carries them, and counts are keyed by glf either way.
        const rMeta = isGetListRecountable(meta) ? meta : recountableMetaByGlf(maps, glf);
        if (rMeta) recountMetas[glf] = rMeta;
    });
    const recountByGlf = {};
    let recountsFired = false;
    const fireRecounts = () => {
        recountsFired = true;
        return Promise.all(
            Object.keys(recountMetas).map(async glf => {
                const res = await fetchDynamicOptionCounts({ meta: recountMetas[glf], conditionParams: postApply });
                if (res) recountByGlf[glf] = res;
            })
        );
    };
    // Predictable triggers — fire in parallel with the catalog fetches: a dynamic dim IN the cascade,
    // a YEAR window, or a WILDCARD in play (selected on the card, or already applied on the dashboard —
    // `raw.filters`). The catalog cannot count wildcards at all, and its year predicate ("any plan row
    // in the window") disagrees with the dashboard's real year filter (verified live: System=Roof +
    // 2026 → the dashboard shows 63, the catalog said 608 for EVERY window; deployed catalogs may still
    // do this until the backend fix ships). /get_list honors both — the wildcard rides the conditioning
    // params as the same JSON `filters` object the dashboard itself sends. The FCI-band-only case
    // surfaces via `uncounted` below and pays one extra round-trip.
    const yearArm = !!full.year;
    const wildcardArm =
        selectedWildcards.length > 0 || !!(rawParams && rawParams.filters && Object.keys(rawParams.filters).length);
    const predictedUncountable = yearArm || wildcardArm || [...dynamicGlfs].some(glf => full.dimensions[glf]);
    const recountPromise = postApply && predictedUncountable ? fireRecounts() : Promise.resolve();

    // The full-cascade call carries the record total (count of everything the Apply would show) + the list
    // of dimensions it could NOT count; the per-field exclusion calls below only need option counts.
    const { total, byGlf: optionsByGlf, uncounted = [] } = await fetchCascadedOptions(scope, full);

    // Same-dimension exclusion for every card field present in the cascade (selected here or already
    // applied on the dashboard): its options must be counted under the OTHER dimensions only.
    const cardGlfs = new Set();
    (allMatches || []).forEach(m => {
        if (!isDimensionPick(m)) return;
        const glf = glfKeyOf(maps && maps.dimMap && maps.dimMap[m.field]);
        if (glf && full.dimensions[glf] && !dynamicGlfs.has(glf)) cardGlfs.add(glf);
    });
    await Promise.all(
        [...cardGlfs].map(async glf => {
            const rest = { ...groups };
            delete rest[glf];
            const partial = mergeSelectionCascade(withoutGlf(baseCascade, glf), rest, year);
            const sub = await fetchCascadedOptions(scope, partial);
            if (sub.byGlf[glf]) optionsByGlf[glf] = sub.byGlf[glf];
        })
    );
    await dynamicPromise;
    await recountPromise;
    // The uncountable arm only showed in the response (FCI band selected, no dynamic dim): re-count now.
    if (uncounted.length && postApply && !recountsFired) await fireRecounts();
    Object.keys(dynamicByGlf).forEach(glf => {
        optionsByGlf[glf] = dynamicByGlf[glf].list;
    });
    Object.keys(recountByGlf).forEach(glf => {
        optionsByGlf[glf] = recountByGlf[glf].list;
    });

    // The catalog's total is UNTRUSTWORTHY under two arms: `uncounted` dimensions (dynamic fields, FCI
    // bands — ignored by the cascade entirely) and a YEAR window (wrong predicate — see above). Recover
    // it from the dashboard's own /get_list instead: pick a dimension we counted ourselves — its call
    // was conditioned on ALL the other post-apply state (postApply models the card-touched dims and the
    // raw params carry the applied ones, uncounted or not) with only its own dimension excluded — and
    // sum its post-apply values' counts. A record holds ONE value per dashboard dimension, so the
    // values are disjoint and the sum IS the record count. Dynamic dims are preferred (longest-proven
    // path), then any re-counted dim. With NO dimension in play (year-only) there is nothing selected
    // to sum, so sum ALL options of the Status dimension: the dashboard's default scope guarantees
    // every visible record has exactly one status, so statuses partition the records. Anything left
    // unrecoverable keeps the total hidden — no number beats a wrong one.
    const catalogTotalTrusted = !uncounted.length && !yearArm && !wildcardArm;
    let trustedTotal = catalogTotalTrusted ? total : null;
    if (!catalogTotalTrusted && postApply) {
        const dimGlfs = Object.keys(full.dimensions)
            .filter(glf => Array.isArray(full.dimensions[glf]) && full.dimensions[glf].length)
            .sort();
        const countingGlf = [...dimGlfs.filter(g => dynamicByGlf[g]), ...dimGlfs.filter(g => !dynamicByGlf[g] && recountByGlf[g])][0];
        if (countingGlf) {
            const res = dynamicByGlf[countingGlf] || recountByGlf[countingGlf];
            trustedTotal = full.dimensions[countingGlf].reduce((sum, v) => sum + (res.byKey[String(v)] || 0), 0);
        } else if (!dimGlfs.length) {
            const statusMeta = recountableMetaByGlf(maps, PARTITION_GLF);
            const res = statusMeta ? await fetchDynamicOptionCounts({ meta: statusMeta, conditionParams: postApply }) : null;
            if (res) trustedTotal = res.list.reduce((sum, o) => sum + (o.count || 0), 0);
        }
    }
    return { ...evaluateFeasibility({ groups, optionsByGlf, allMatches, maps }), total: trustedTotal, uncounted };
}
